import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '../../images/trash.png';

// Components
import BaseContentContainer from '../../components/base-content-container/base-content-container';
import ButtonsBar from '../../components/buttons-bar/buttons-bar';
import ErrorComponent from '../../components/error/error';
import FeatureTitle from '../../components/feature-title/feature-title';
import Infos from '../../components/infos/infos';
import Loading from '../../components/loading/loading';
import SubmitButton from '../../components/submit-button/submit-button';

// Helpers
import {
  getEmployeeRole,
  getPeriodName,
} from '../../helpers/string-format-helper';
import {
  getDefaultCreateClassroomData,
  openCreateClassroomModal,
} from '../../helpers/management-classroom-helper';

// Models
import {
  BaseContentContainerActionButtonPosition,
  BaseContentContainerFormat,
} from '../../models/base-content-container';

// Providers
import { GeneralContext } from '../../providers/general-provider';

// Services
import ManagementService from '../../services/management-service';
import { validateEmail, validatePassword } from '../../helpers/validate-helper';

const ManagementUsers = () => {
  const navigate = useNavigate();
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const managementService = new ManagementService();

  const [error, setError] = useState(false);

  // CLASSROOM
  const [classroomData, setClassroomData] = useState<InfosDataList>(null);
  const updateClassroomData = () => {
    managementService
      .getClassrooms()
      .then((classrooms) => {
        const newClassroomData: InfosDataList = {
          type: 'list',
          content: {
            titles: [
              {
                text: 'Turma',
              },
              {
                text: 'Período',
                customStyle: { textAlign: 'right' },
              },
            ],
            rows: classrooms.map((classroom) => [
              {
                text: classroom.name,
                onClick: () => navigate(`/gerenciar/turma/${classroom.id}`),
              },
              {
                text: getPeriodName(classroom.period),
                customStyle: { textAlign: 'right' },
                onClick: () => navigate(`/gerenciar/turma/${classroom.id}`),
              },
            ]),
          },
        };

        setClassroomData(newClassroomData);
      })
      .catch(() => setError(true));
  };
  useEffect(() => {
    updateClassroomData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultCreateClassroomData: CreateClassroomFormData = getDefaultCreateClassroomData();
  const [createClassroomData, setCreateClassroomData] = useState(
    defaultCreateClassroomData
  );

  const createClassroomPromise = (data: CreateClassroomFormData) => {
    if (!data.name || !data.period || !data.diaryMode)
      return Promise.reject('Todos os campos devem ser preenchidos');

    return managementService
      .createNewClassroom(data)
      .then(() => {
        setToastContent({
          isOpen: true,
          text: 'Turma criada!',
          type: 'success',
        });

        setCreateClassroomData(defaultCreateClassroomData);
        updateClassroomData();
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: 'Houve um erro ao criar a turma.',
          type: 'error',
        });
      });
  };

  // EMPLOYEE
  const [employeeData, setEmployeeData] = useState<InfosDataList>(null);

  const deleteEmployee = (employee: Employee) => {
    const deletePromise = ({ employeeId }: { employeeId: number }) => {
      return managementService
        .deleteEmployee(employeeId)
        .then(() => {
          setToastContent({
            isOpen: true,
            text: 'Funcionário deletado',
            type: 'success',
          });

          updateEmployeeData();
        })
        .catch(() => {
          setToastContent({
            isOpen: true,
            text: 'Houve um erro ao deletar o funcionário.',
            type: 'error',
          });
        });
    };

    setModalContent({
      title: `Deletar funcionário`,
      modalContent: (
        <p>
          Deseja realmente deletar o funcionário <b>{employee.name}</b>
        </p>
      ),
      submitButtonTitle: 'confirmar deleção',
      submitButtonType: 'primary',
      submitData: { employeeId: employee.id },
      onSubmitButtonClick: deletePromise,
      isOpen: true,
    });
  };

  const updateEmployeeData = () => {
    managementService
      .getEmployees()
      .then((employees) => {
        const newEmployeeData: InfosDataList = {
          type: 'list',
          content: {
            titles: [
              {
                text: 'Funcionário',
              },
              {
                text: 'Função',
                customStyle: { textAlign: 'center' },
              },
              {
                text: '',
              },
            ],
            rows: employees.map((employee) => [
              {
                text: employee.name,
                onClick: () =>
                  navigate(`/funcionarios/${employee.id}`),
              },
              {
                text: getEmployeeRole(employee.role),
                customStyle: { textAlign: 'center' },
                onClick: () =>
                    navigate(`/funcionarios/${employee.id}`),
              },
            ]),
          },
        };

        setEmployeeData(newEmployeeData);
      })
      .catch(() => setError(true));
  };
  useEffect(() => {
    updateEmployeeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const defaultCreateEmployeeData: CreateEmployeeFormData = {
    id: null,
    name: null,
    email: null,
    password: null,
    role: null,
  };
  const [createEmployeeData, setCreateEmployeeData] = useState(
    defaultCreateEmployeeData
  );

  const employeePromise = (data: CreateEmployeeFormData) => {
    let submitPromise;
    if (data.id) {
      if (!data.name || !data.email || !data.role)
        return Promise.reject(
          'Os campos Nome, Email e Função são obrigatórios'
        );

      submitPromise = managementService.updateEmployee;
    } else {
      if (!data.name || !data.email || !data.password || !data.role)
        return Promise.reject('Todos os campos devem ser preenchidos');

      submitPromise = managementService.createNewEmployee;
    }

    const emailValidate = validateEmail(data.email);
    if (!emailValidate.isValid) return Promise.reject(emailValidate.message);

    if (data.password && data.password.length > 0) {
      const passwordValidate = validatePassword(data.password);
      if (!passwordValidate.isValid)
        return Promise.reject(passwordValidate.message);

      if (data.password.length < 8)
        return Promise.reject('Senha deve conter no mínimo 8 caracteres.');
    }

    return submitPromise(data, data.id)
      .then(() => {
        setToastContent({
          isOpen: true,
          text: `Funcionário ${
            typeof data.id === 'number' ? 'atualizado' : 'adicionado'
          }!`,
          type: 'success',
        });

        setCreateEmployeeData(defaultCreateEmployeeData);
        updateEmployeeData();
      })
      .catch(() => {
        setToastContent({
          isOpen: true,
          text: `Houve um erro ao ${
            typeof data.id === 'number' ? 'atualizar' : 'adicionar'
          } o funcionário.`,
          type: 'error',
        });
      });
  };

  const openCreateEmployeeModal = (
    data: CreateEmployeeFormData,
    editData?: Employee
  ) => {
    if (editData) {
      if (!data.name || !data.email || !data.role || !data.id) {
        const newEmployeeData: CreateEmployeeFormData = {
          id: editData.id,
          name: editData.name,
          email: editData.email,
          password: null,
          role: editData.role,
        };

        setCreateEmployeeData(newEmployeeData);
        openCreateEmployeeModal(newEmployeeData);

        return;
      }
    }

    const resetFormData = () => {
      return new Promise<void>((resolve) => {
        setCreateEmployeeData(defaultCreateEmployeeData);

        resolve();
      });
    };

    const title =
      typeof data.id === 'number'
        ? 'Atualizar funcionário'
        : 'Adicionar funcionário';
    const submitButtonTitle =
      typeof data.id === 'number'
        ? 'atualizar funcionário'
        : 'adicionar funcionário';

    setModalContent({
      title,
      modalContent: renderCreateEmployee(data),
      submitButtonTitle,
      submitButtonType: 'primary',
      submitData: data,
      onSubmitButtonClick: employeePromise,
      onCloseModal: resetFormData,
      isOpen: true,
    });
  };

  const handleCreateEmployeeInputChange = (
    evt:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>,
    data: CreateEmployeeFormData
  ) => {
    const newData = {
      ...data,
      [evt.target.id]: evt.target.value,
    };

    setCreateEmployeeData(newData);
    openCreateEmployeeModal(newData);
  };

  const renderCreateEmployee = (data: CreateEmployeeFormData) => {
    return (
      <div>
        <label htmlFor='name'>Nome</label>
        <input
          id='name'
          name='name'
          placeholder='Nome'
          type='text'
          onChange={(evt) => handleCreateEmployeeInputChange(evt, data)}
          defaultValue={data.name ? data.name : null}
        />

        <label htmlFor='email'>Email</label>
        <input
          id='email'
          name='email'
          placeholder='Email'
          type='text'
          onChange={(evt) => handleCreateEmployeeInputChange(evt, data)}
          defaultValue={data.email ? data.email : null}
          autoCapitalize='none'
          autoCorrect='off'
        />

        <label htmlFor='password'>Senha</label>
        <input
          id='password'
          name='password'
          placeholder='Senha'
          type='password'
          onChange={(evt) => handleCreateEmployeeInputChange(evt, data)}
        />

        <label htmlFor='role'>Função</label>
        <select
          name='role'
          id='role'
          defaultValue={data.role ? data.role : 'default'}
          onChange={(evt) => handleCreateEmployeeInputChange(evt, data)}
        >
          <option value='default' disabled hidden>
            Selecionar
          </option>
          <option value='Administrator'>
            {getEmployeeRole('Administrator')}
          </option>
          <option value='Teacher'>{getEmployeeRole('Teacher')}</option>
        </select>
      </div>
    );
  };

  if (error) return <ErrorComponent type='contentFull' />;

  if (!classroomData || !employeeData) return <Loading type='contentFull' />;

  return (
    <BaseContentContainer
      format={BaseContentContainerFormat.doubleWithoutActionButtons}
      withoutShadowBox={true}
    >
      <BaseContentContainer
        format={BaseContentContainerFormat.singleWithActionButtons}
        actionButtonPosition={BaseContentContainerActionButtonPosition.top}
        withoutShadowBox={true}
        customContainerStyle={{ paddingRight: '45px' }}
      >
        <ButtonsBar pattern='firstSeparate'>
          <FeatureTitle title='turmas' />
          <SubmitButton
            value='adicionar turma'
            variant='primary'
            onClick={() =>
              openCreateClassroomModal(
                createClassroomData,
                defaultCreateClassroomData,
                setCreateClassroomData,
                setModalContent,
                createClassroomPromise
              )
            }
          />
        </ButtonsBar>
        <Infos backgroundColor='white' data={classroomData} />
      </BaseContentContainer>

      <BaseContentContainer
        format={BaseContentContainerFormat.singleWithActionButtons}
        actionButtonPosition={BaseContentContainerActionButtonPosition.top}
        withoutShadowBox={true}
        customContainerStyle={{ paddingLeft: '45px' }}
      >
        <ButtonsBar pattern='firstSeparate'>
          <FeatureTitle title='funcionários' />
          <SubmitButton
            value='adicionar funcionário'
            variant='primary'
            onClick={() => navigate('/funcionarios/criar')}
          />
        </ButtonsBar>
        <Infos backgroundColor='white' data={employeeData} />
      </BaseContentContainer>
    </BaseContentContainer>
  );
};

export default ManagementUsers;
